import {DocumentDuplicateIcon, ExternalLinkIcon} from '@heroicons/react/solid';

import {Button, Link} from '@/components';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {copyToClipboard} from '@/utils/user';

type MerchantUserSummary = {
  user: AfriexBusinessUser;
  business: AfriexBusiness;
  extraRow?: React.ReactNode;
  iskyckybScreen?: boolean;
};

// The email shares one cell width, so anything longer is clipped with an
// ellipsis; a missing value shows the same placeholder the other rows use.
const truncateEmail = (email?: string | null): string => {
  if (!email) {
    return '-';
  }

  return email.length > 15 ? `${email.slice(0, 15)}...` : email;
};

const MerchantUserSummary = ({
  user,
  business,
  extraRow,
  iskyckybScreen,
}: MerchantUserSummary) => {
  const userEmail = user?.email || null;
  return (
    <div
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
      data-cy="merchant-user-summary"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-2xl mr-3">{user.name?.fullName}</h2>
        <Link
          to={`/merchants/users/${user.id}/statement`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-sm text-cyan-700 gap-2 hover:underline"
          data-cy="merchant-user-summary-statement-link"
        >
          <ExternalLinkIcon className="w-4 h-4" />
          View Statement
        </Link>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          User ID
        </p>
        <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
          <div className="flex-1 flex items-center gap-2 truncate">
            <span className="truncate" data-cy="merchant-user-summary-user-id">
              {user.id}
            </span>
            <span
              title="click to copy userID"
              onClick={() => copyToClipboard(user.id)}
              data-cy="merchant-user-summary-copy-user-id"
            >
              <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
            </span>
          </div>
        </div>
      </div>
      {!iskyckybScreen ? (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Business ID
          </p>
          <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
            <div className="flex-1 flex items-center gap-2 truncate">
              <span className="truncate">{business.id}</span>
              <span
                title="click to copy userID"
                onClick={() => copyToClipboard(business?.id || '')}
              >
                <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
              </span>
            </div>
          </div>
        </div>
      ) : null}
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
          Account Email
        </p>
        <div className="flex-[0.6] text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
          <div className="flex items-center gap-2 truncate">
            <span className="truncate" data-cy="merchant-user-summary-email">
              {truncateEmail(userEmail)}
            </span>
            {userEmail && (
              <span
                className="truncate"
                title="click to copy email"
                onClick={() => copyToClipboard(userEmail)}
                data-cy="merchant-user-summary-copy-email"
              >
                <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Business Name
        </p>
        <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
          <div className="flex-1 flex items-center gap-2 truncate">
            {business.name}
          </div>
        </div>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          User Profile
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/users/${user.id}`}
            target="_blank"
            rel="noopener noreferrer hover:underline"
            data-cy="merchant-user-summary-profile-link"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View User Profile
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Business Profile
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/businesses/${business.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View Business Profile
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Transactions
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/transactions/business/${business.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View Transactions
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          KYC Details
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/kyc/${user.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="merchant-user-summary-kyc-link"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View KYC Details
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Role
        </p>
        <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
          <div className="flex-1 flex items-center gap-2 truncate">
            {user.role}
          </div>
        </div>
      </div>
      {extraRow || ''}
    </div>
  );
};

export default MerchantUserSummary;
